import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoIcon from "@mui/icons-material/Info";
import { NotInterested } from "@mui/icons-material";
import {
	Autocomplete,
	Chip,
	Divider,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Popover,
	Stack,
	TextField,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { visibleNavSections } from "@domain/Auth";
import { useAuthUseCases } from "@gui/contexts/useCases/UseCasesContext";
import { useAirtableStatus } from "@gui/hooks/useAirtableStatus";
import { AppVersion } from "@gui/widgets/AppVersion";
import { API } from "@gui/infrastructure/pass-through/api";
import { MENU_LINKS } from "./navLinks";
import { UserContext } from "@gui/contexts/user/UserContext";
import { useCompanyContext } from "@gui/contexts/company";
import { useImpersonationContext } from "@gui/contexts/impersonation";
import { useImpersonationSelector } from "./useImpersonationSelector";
import type { ImpersonatableUser } from "@gui/infrastructure/pass-through/api";

interface ButtonAppBarProps {
	title: string;
	onMenuClick?: () => void;
}

// White-on-AppBar input styling — applied only when the selector sits on the dark toolbar (desktop).
// Inside the overflow Popover (mobile) the selectors use the default dark-on-light theme colors.
const darkInputSx = {
	"& .MuiOutlinedInput-root": {
		color: "white",
		"& fieldset": { borderColor: "rgba(255,255,255,0.5)" },
		"&:hover fieldset": { borderColor: "white" },
		"&.Mui-focused fieldset": { borderColor: "white" },
	},
	"& .MuiInputLabel-root": { color: "rgba(255,255,255,0.7)" },
	"& .MuiInputLabel-root.Mui-focused": { color: "white" },
	"& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.7)" },
} as const;

const STATUS_COLORS: Record<string, string> = {
	none: "#4caf50",
	minor: "#ff9800",
	major: "#f44336",
	critical: "#f44336",
};

// Airtable status row for the account menu. Kept as its own component so the polling
// hook (useAirtableStatus) only mounts when the admin-only item is rendered.
function AirtableStatusMenuItem({ onClose }: { onClose: () => void }) {
	const { indicator } = useAirtableStatus();
	const color = indicator ? STATUS_COLORS[indicator] : "#9e9e9e";
	return (
		<MenuItem
			onClick={() => {
				window.open("https://status.airtable.com", "_blank", "noopener,noreferrer");
				onClose();
			}}
		>
			<ListItemIcon>
				<Box sx={{ width: 24, display: "flex", justifyContent: "center" }}>
					<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: color }} />
				</Box>
			</ListItemIcon>
			<ListItemText>Airtable Status</ListItemText>
		</MenuItem>
	);
}

export function CustomToolbar(props: ButtonAppBarProps) {
	const theme = useTheme();
	const user = useContext(UserContext);
	const { companies, selectedCompany, setSelectedCompany } = useCompanyContext();

	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const showCompanySelector = user?.isAdmin && companies.length > 1;
	const { isImpersonating } = useImpersonationContext();
	const showImpersonationSelector = user?.isDeveloper || isImpersonating;
	const { impersonatedEmail, startImpersonation, stopImpersonation, users, isLoading, fetchUsers } =
		useImpersonationSelector(selectedCompany?.id, showImpersonationSelector);

	const [overflowAnchor, setOverflowAnchor] = useState<HTMLElement | null>(null);
	const [accountMenuAnchor, setAccountMenuAnchor] = useState<HTMLElement | null>(null);

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const companyParam = searchParams.get("company");
	const buildNavPath = (path: string) => (companyParam ? `${path}?company=${companyParam}` : path);

	const { auth } = useAuthUseCases();
	const showLogout = visibleNavSections(user).showLogout;
	const handleLogout = () => {
		auth.logout();
		// Hard reload to /login so all in-memory caches reset alongside the cleared tokens.
		window.location.href = "/login";
		setAccountMenuAnchor(null);
	};

	// Whether there is anything to show in the toolbar controls cluster.
	const hasControls = showCompanySelector || user?.isDeveloper || (showImpersonationSelector && isImpersonating);

	const companySelector = showCompanySelector && (
		<Autocomplete
			size="small"
			options={companies}
			value={selectedCompany}
			getOptionKey={(option) => option.id}
			getOptionLabel={(option) => option.name}
			isOptionEqualToValue={(option, value) => option.id === value.id}
			onChange={(_, value) => setSelectedCompany(value)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Company"
					variant="outlined"
					size="small"
					sx={isMobile ? undefined : darkInputSx}
				/>
			)}
			sx={isMobile ? { width: "100%" } : { minWidth: { xs: 150, sm: 220 }, maxWidth: 300, flexShrink: 1 }}
		/>
	);

	const impersonationChip = showImpersonationSelector && isImpersonating && (
		<Chip
			label={`Impersonating: ${impersonatedEmail}`}
			color="warning"
			size="small"
			onDelete={stopImpersonation}
			sx={{ fontWeight: "bold", ...(isMobile ? { alignSelf: "flex-start" } : { ml: 1 }) }}
		/>
	);

	const impersonationSelector = user?.isDeveloper && (
		<Autocomplete<ImpersonatableUser>
			size="small"
			options={users}
			loading={isLoading}
			value={
				impersonatedEmail
					? (users.find((u) => u.email === impersonatedEmail) ?? {
							id: impersonatedEmail,
							email: impersonatedEmail,
							name: impersonatedEmail,
							companyIds: [],
						})
					: null
			}
			getOptionKey={(option) => option.id}
			getOptionLabel={(option) => `${option.name} (${option.email})`}
			isOptionEqualToValue={(option, value) => option.email === value.email}
			onOpen={fetchUsers}
			onChange={(_, value) => {
				if (value) {
					startImpersonation(value.email);
				} else {
					stopImpersonation();
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Impersonate User"
					variant="outlined"
					size="small"
					sx={isMobile ? undefined : darkInputSx}
				/>
			)}
			sx={isMobile ? { width: "100%" } : { minWidth: { xs: 150, sm: 250 }, maxWidth: 350, flexShrink: 1, ml: 1 }}
		/>
	);

	return (
		<Box style={{ zIndex: theme.zIndex.drawer + 1 }}>
			<AppBar position="static">
				<Toolbar>
					{isMobile && (
						<IconButton
							size="large"
							edge="start"
							color="inherit"
							aria-label="menu"
							onClick={props.onMenuClick}
							sx={{ mr: 2 }}
						>
							<MenuIcon />
						</IconButton>
					)}
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
						{props.title}
					</Typography>

					{/* Desktop: render the selectors inline on the AppBar. */}
					{!isMobile && (
						<>
							{companySelector}
							{impersonationChip}
							{impersonationSelector}
						</>
					)}

					{/* Mobile: collapse the selectors into an overflow menu so they don't crowd the bar. */}
					{isMobile && hasControls && (
						<>
							<IconButton
								color="inherit"
								aria-label="account settings"
								onClick={(e) => setOverflowAnchor(e.currentTarget)}
							>
								<MoreVertIcon />
							</IconButton>
							<Popover
								open={Boolean(overflowAnchor)}
								anchorEl={overflowAnchor}
								onClose={() => setOverflowAnchor(null)}
								anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
								transformOrigin={{ vertical: "top", horizontal: "right" }}
							>
								<Stack spacing={2} sx={{ p: 2, width: 280 }}>
									{companySelector}
									{impersonationChip}
									{impersonationSelector}
								</Stack>
							</Popover>
						</>
					)}

					{/* Account menu: holds less-used items (Profile, …) moved off the sidebar. */}
					<IconButton
						color="inherit"
						aria-label="account menu"
						onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
						sx={{ ml: 1 }}
					>
						<PersonIcon />
					</IconButton>
					<Menu
						anchorEl={accountMenuAnchor}
						open={Boolean(accountMenuAnchor)}
						onClose={() => setAccountMenuAnchor(null)}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						transformOrigin={{ vertical: "top", horizontal: "right" }}
					>
						{MENU_LINKS.map((link) => (
							<MenuItem
								key={link.to}
								onClick={() => {
									navigate(buildNavPath(link.to), { state: { title: link.text } });
									setAccountMenuAnchor(null);
								}}
							>
								{link.icon && <ListItemIcon>{link.icon}</ListItemIcon>}
								<ListItemText>{link.text}</ListItemText>
							</MenuItem>
						))}
						{showLogout && (
							<MenuItem key="logout" onClick={handleLogout}>
								<ListItemIcon>
									<LogoutIcon />
								</ListItemIcon>
								<ListItemText>Logout</ListItemText>
							</MenuItem>
						)}
						{user?.isAdmin && [
							<Divider key="admin-divider" />,
							<MenuItem
								key="clear-cache"
								onClick={() => {
									API.Data.clearCache();
									setAccountMenuAnchor(null);
								}}
							>
								<ListItemIcon>
									<NotInterested />
								</ListItemIcon>
								<ListItemText>Clear Portal Cache</ListItemText>
							</MenuItem>,
							<AirtableStatusMenuItem key="airtable-status" onClose={() => setAccountMenuAnchor(null)} />,
						]}
						<Divider />
						<MenuItem disabled sx={{ "&.Mui-disabled": { opacity: 1 } }}>
							<ListItemIcon>
								<InfoIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText
								primary={<AppVersion />}
								sx={{ "& .MuiListItemText-primary": { color: "text.secondary" } }}
							/>
						</MenuItem>
					</Menu>
				</Toolbar>
			</AppBar>
		</Box>
	);
}
